import type { OrderItem, FulfillmentMode } from '../types';
import {
  applyLineDiscountAmountPerUnit,
  applyLineDiscountPercent,
  applyLinePrice,
  applyLineQuantity,
} from './lineValueCalc';

/**
 * TCR-2425 — "Sao chép dữ liệu cho các dòng bên dưới" (chuột phải dòng hàng ở
 * đơn B2B, tham khảo AMIS).
 *
 * Hàm THUẦN: nhận dòng nguồn + danh sách dòng đích + các cột cần sao chép,
 * trả về patch cho từng dòng đích. Không gọi API, không message, không đụng
 * state — nhờ vậy toàn bộ quy tắc (thứ tự áp, kẹp trần, dòng bị bỏ qua) test
 * được mà không cần render bảng 2.700 dòng.
 *
 * Ba nguyên tắc, đúng thứ tự thực thi:
 *
 * 1. Dòng đích bị loại từ đầu, áp cho MỌI cột: dòng quà tặng (`is_gift`) và
 *    dòng thuộc cụm combo (`comboGroupId`). Đây là hai loại dòng mà chính
 *    `OrderItemRow` cũng khoá ô nhập, nên "sao chép" vào chúng là ghi đè thứ
 *    thu ngân không sửa được bằng tay ⇒ sai tiền mà không có đường lùi.
 * 2. Áp TUẦN TỰ Số lượng → Đơn giá → Chiết khấu trên cùng một bản làm việc
 *    của dòng đích. Thứ tự này bắt buộc: thành tiền = đơn giá × SL, còn CK %
 *    tính trên thành tiền; làm ngược lại sẽ ra số của dòng cũ.
 * 3. Patch cuối chỉ chứa trường ĐỔI. `onUpdateItem` được gọi cho từng dòng,
 *    gửi kèm cả chục trường không đổi sẽ làm dòng "nhảy" (recalc ngoài ý muốn)
 *    và làm message "Đã sao chép cho N dòng" đếm sai.
 *
 * Quy ước CK dạng TIỀN: giá trị được sao chép là số tiền MỖI ĐƠN VỊ
 * (`source.discountAmount / source.quantity`), KHÔNG phải tổng tiền CK của
 * dòng nguồn — xem `applyLineDiscountAmountPerUnit`.
 */

/** Lý do một dòng (hoặc một cột của dòng) không được sao chép. */
export const COPY_LINE_SKIP_REASON = {
  GIFT: 'Dòng quà tặng',
  COMBO: 'Dòng combo',
  FLASH_SALE: 'Dòng flash sale',
  SERIAL: 'Hàng theo serial',
  NO_DISCOUNT_PERMISSION: 'Không có quyền sửa giá/chiết khấu',
  NO_SOURCE_QUANTITY: 'Dòng nguồn không có số lượng để quy chiết khấu tiền ra đơn vị',
} as const;

export type CopyLineSkipReason =
  (typeof COPY_LINE_SKIP_REASON)[keyof typeof COPY_LINE_SKIP_REASON];

/** Cột được tick trong hộp "Chọn cột sao chép dữ liệu". */
export interface CopyLineColumns {
  quantity: boolean;
  price: boolean;
  discount: boolean;
}

export interface CopyLineValuesParams {
  /** Dòng được chuột phải — nguồn của mọi giá trị. */
  source: OrderItem;
  /** Các dòng nằm SAU dòng nguồn theo thứ tự hiển thị (không gồm chính nó). */
  targets: OrderItem[];
  columns: CopyLineColumns;
  fulfillmentMode: FulfillmentMode;
  isB2B: boolean;
  /** Quyền `pos.discount` — thiếu quyền thì không được đụng Đơn giá/Chiết khấu. */
  hasDiscountPermission: boolean;
  /** Trần SL của từng dòng (màn sửa đơn khoá dòng flash đã hết hạn) — giống `handleQtyBlur`. */
  maxQuantityFor?: (item: OrderItem) => number | undefined;
  /**
   * Dòng flash sale ĐÃ LƯU bị khoá sửa hoàn toàn (`Orders/edit.tsx`) — giống
   * `isLineLocked` trong `OrderItemRow`: không cột nào được sao chép vào.
   */
  lockFlashSaleLines?: boolean;
}

export interface CopyLineSkipped {
  id: string;
  reason: string;
}

export interface CopyLineValuesResult {
  /** id dòng đích → patch cần gửi `onUpdateItem`. */
  patches: Record<string, Partial<OrderItem>>;
  /** id các dòng thực sự được cập nhật (dùng cho message "Đã sao chép cho N dòng"). */
  applied: string[];
  /** id các dòng không nhận được gì + lý do (dùng cho message cảnh báo). */
  skipped: CopyLineSkipped[];
}

/** Dòng flash sale: nhận diện qua cả 2 field như `OrderItemRow.isFlashLine`. */
function isFlashSaleLine(item: OrderItem): boolean {
  return Boolean(item.flash_sale_item_id || item.flash_sale_id);
}

/** Dòng theo serial mà chế độ giao hiện tại không cho đổi SL tự do. */
function isSerialLocked(item: OrderItem, fulfillmentMode: FulfillmentMode): boolean {
  return item.trackingMode !== 'quantity' && fulfillmentMode !== 'PREORDER';
}

/** Các trường có thể bị copy đổi — dùng để diff ra patch cuối. */
const PATCH_KEYS = [
  'quantity',
  'price',
  'discountType',
  'discountPercent',
  'discountAmount',
  'totalAmount',
  'hasPriceOverride',
  'hasDiscountOverride',
] as const;

/**
 * Diff bản làm việc với dòng gốc. So sánh từng trường bằng `Object.is` (đủ cho
 * number/string/boolean); trường không đổi bị bỏ khỏi patch.
 */
function diffPatch(original: OrderItem, next: OrderItem): Partial<OrderItem> {
  const patch: Partial<OrderItem> = {};
  for (const key of PATCH_KEYS) {
    if (!Object.is(original[key], next[key])) {
      // `key` là union của các khoá trên nên phép gán hợp lệ; TS không tự thu
      // hẹp được kiểu giá trị theo từng khoá nên cần ép qua `Partial<OrderItem>`.
      (patch as Record<string, unknown>)[key] = next[key];
    }
  }
  return patch;
}

export function copyLineValues({
  source,
  targets,
  columns,
  fulfillmentMode,
  isB2B,
  hasDiscountPermission,
  maxQuantityFor,
  lockFlashSaleLines,
}: CopyLineValuesParams): CopyLineValuesResult {
  const result: CopyLineValuesResult = { patches: {}, applied: [], skipped: [] };
  // Không tick cột nào ⇒ không làm gì (UI chặn nút "Sao chép", hàm vẫn phải an toàn).
  if (!columns.quantity && !columns.price && !columns.discount) return result;

  for (const target of targets) {
    // (1) Dòng bị loại cho MỌI cột.
    if (target.is_gift) {
      result.skipped.push({ id: target.id, reason: COPY_LINE_SKIP_REASON.GIFT });
      continue;
    }
    if (target.comboGroupId) {
      result.skipped.push({ id: target.id, reason: COPY_LINE_SKIP_REASON.COMBO });
      continue;
    }
    // Dòng flash đã bị khoá sửa (màn sửa đơn): giống `isLineLocked`, mọi ô đều
    // disabled nên sao chép vào là ghi đè ngoài tầm kiểm soát của thu ngân.
    const flashLine = isFlashSaleLine(target);
    if (lockFlashSaleLines && flashLine) {
      result.skipped.push({ id: target.id, reason: COPY_LINE_SKIP_REASON.FLASH_SALE });
      continue;
    }

    // (2) Áp tuần tự trên bản làm việc; lý do của từng cột bị từ chối được gom
    // lại để báo "bỏ qua dòng" khi không cột nào áp được.
    const refused: string[] = [];
    let work = target;

    if (columns.quantity) {
      if (isSerialLocked(work, fulfillmentMode)) {
        refused.push(COPY_LINE_SKIP_REASON.SERIAL);
      } else if (flashLine) {
        // Suất KM của dòng flash do CẢ NHÓM quyết định (xem `rebalanceFlashGroup`)
        // — gán SL thô từ dòng khác sẽ phá phân bổ flash mà không rebalance.
        refused.push(COPY_LINE_SKIP_REASON.FLASH_SALE);
      } else {
        const capped = maxQuantityFor?.(work);
        const quantity =
          typeof capped === 'number' ? Math.min(source.quantity, capped) : source.quantity;
        work = applyLineQuantity(work, quantity, fulfillmentMode);
      }
    }

    if (columns.price) {
      if (!hasDiscountPermission) {
        refused.push(COPY_LINE_SKIP_REASON.NO_DISCOUNT_PERMISSION);
      } else {
        work = applyLinePrice(work, source.price, fulfillmentMode, isB2B);
      }
    }

    if (columns.discount) {
      if (!hasDiscountPermission) {
        if (!refused.includes(COPY_LINE_SKIP_REASON.NO_DISCOUNT_PERMISSION)) {
          refused.push(COPY_LINE_SKIP_REASON.NO_DISCOUNT_PERMISSION);
        }
      } else if (source.discountType === 'amount') {
        if (source.quantity > 0) {
          // Quy ước CK tiền theo ĐƠN VỊ: tiền CK/đv của nguồn mới là thứ mang
          // nghĩa "cùng một chiết khấu", tổng tiền CK của nguồn thì không.
          work = applyLineDiscountAmountPerUnit(
            work,
            source.discountAmount / source.quantity,
            fulfillmentMode,
          );
          work = { ...work, hasDiscountOverride: true };
        } else if (!refused.includes(COPY_LINE_SKIP_REASON.NO_SOURCE_QUANTITY)) {
          // Codex review (Major): SL nguồn = 0 thì không chia được CK/đơn vị — KHÔNG được rơi
          // sang nhánh % (discountPercent cũ của nguồn tiền không còn nghĩa), bỏ qua cột này.
          refused.push(COPY_LINE_SKIP_REASON.NO_SOURCE_QUANTITY);
        }
      } else {
        work = applyLineDiscountPercent(work, source.discountPercent, fulfillmentMode);
        work = { ...work, discountType: 'percent', hasDiscountOverride: true };
      }
    }

    // (3) Patch cuối = đúng các trường đã đổi.
    const patch = diffPatch(target, work);
    if (Object.keys(patch).length > 0) {
      result.patches[target.id] = patch;
      result.applied.push(target.id);
    } else if (refused.length > 0) {
      result.skipped.push({ id: target.id, reason: refused.join('; ') });
    }
  }

  return result;
}
